import { Link } from "react-router-dom";
import { ShieldCheck, Star, MessageSquare, CheckCircle } from "lucide-react";

export default function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-50 lg:flex relative overflow-hidden">
      {/* Mobile background accents */}
      <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 lg:hidden" />
      <div className="absolute top-12 right-[-60px] h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl lg:hidden" />
      <div className="absolute top-32 left-[-60px] h-40 w-40 rounded-full bg-blue-400/20 blur-3xl lg:hidden" />

      {/* Desktop Brand Panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white p-12 flex-col justify-between">
        <div className="absolute top-20 right-[-80px] h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
        <div className="absolute bottom-20 left-[-80px] h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" />

        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 w-fit">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/10 border border-white/10">
              <ShieldCheck size={24} />
            </div>

            <h1 className="font-heading text-2xl font-bold tracking-tight">
              RevUShield
            </h1>
          </Link>
        </div>

        <div className="relative z-10 space-y-8">
          {/* <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-indigo-100">
            <Sparkles size={16} />
            AI-powered reputation workflow
          </div> */}

          <div>
            <h1 className="text-3xl xl:text-4xl font-bold leading-tight">
              Keep every review response professional, consistent, and on-brand.
            </h1>

            <p className="text-slate-300 mt-5 text-lg leading-relaxed">
              Draft, approve, and manage Google review responses with a clean
              workflow built for local businesses.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-2xl max-w-lg">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center font-semibold">
                SM
              </div>

              <div className="flex-1">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={15}
                      className="fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                <p className="text-sm text-slate-200 mt-3">
                  “The team was friendly, fast, and made everything easy.”
                </p>

                <div className="mt-4 rounded-xl bg-indigo-500/20 border border-indigo-300/20 p-3">
                  <div className="flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1">
                    <MessageSquare size={14} />
                    Suggested Response
                  </div>

                  <p className="text-sm text-slate-100">
                    Thank you so much! We’re glad our team made your experience
                    easy and appreciate your support.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-lg">
            <MiniStat value="4.7" label="Avg Rating" />
            <MiniStat value="92%" label="Response Rate" />
            <MiniStat value="18" label="Need Reply" />
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-sm text-slate-400">
          <p>© {new Date().getFullYear()} RevUShield</p>

          <div className="flex items-center gap-2 text-green-300">
            <CheckCircle size={15} />
            Google-ready workflow
          </div>
        </div>
      </div>

      {/* Auth Form Side */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-6 py-10">
        <div className="w-full max-w-md">
          {/* Mobile Brand */}
          <div className="lg:hidden text-center mb-8 text-white">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/10 border border-white/10">
                <ShieldCheck size={24} />
              </div>

              <span className="text-3xl font-bold tracking-tight">
                RevUShield
              </span>
            </Link>

            <p className="text-sm text-slate-300 mt-3">
              AI review responses with approval built in.
            </p>
          </div>

          <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/5 p-6 sm:p-8">
            <div className="mb-6">
              <div className="h-11 w-11 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
                <ShieldCheck size={22} />
              </div>

              <h1 className="text-2xl font-bold text-slate-900">{title}</h1>

              <p className="text-slate-500 mt-2 leading-relaxed">{subtitle}</p>
            </div>

            {children}
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">
            Protected by secure authentication and approval-first workflows.
          </p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ value, label }) {
  return (
    <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-slate-300 mt-1">{label}</p>
    </div>
  );
}
